import type { SagaIterator } from 'redux-saga';
import { call, put } from 'redux-saga/effects';
import { setError } from '../store/appSlice';
import type { Cart, CartPatch } from '../types/cart';
import type { CatalogItem } from '../types/catalogItem';
import type { Country } from '../types/country';
import type { CustomerInfo } from '../types/customerInfo';
import type { Localization } from '../types/localization';
import type {
  Order,
  OrderFault,
  OrderRequest,
  PageSignature,
  PayPalBaidResponse,
  PayPalInitResponse,
} from '../types/payment';
import type { Preview, PreviewRequest } from '../types/preview';
import type { ProductCmsData } from '../types/productCms';
import type { Session } from '../types/session';
import type { TrialCmsData } from '../types/trial';
import api, { ApiError } from '../utils/api';
import { getPaymentDecline } from './payment/gatewayResponse';

export function* getSession(): SagaIterator<Session | null> {
  return (yield call(api.fetch, `${window.SSO}/session`)) as Session | null;
}

export function* getLocalization(): SagaIterator<Localization | null> {
  return (yield call(
    api.fetch,
    `${window.ORDERS}/localization`,
  )) as Localization | null;
}

export function* getCountries(): SagaIterator<Country[] | null> {
  return (yield call(api.fetch, `${window.SSO}/countries`)) as Country[] | null;
}

export function* getCart(cartId?: string): SagaIterator<Cart | null> {
  const queryStr = cartId ? `?cartUUID=${cartId}` : '';
  const cart = (yield call(
    api.fetch,
    `${window.ORDERS}/cart${queryStr}`,
  )) as Cart;
  return cart;
}

export function* getCustomerInfo(): SagaIterator<CustomerInfo | null> {
  return (yield call(api.fetch, `${window.ORDERS}/payments/customer-info`, {
    errorMessage: 'Billing info failed.',
  })) as CustomerInfo | null;
}

export function* getCatalogData(
  ratePlans: string[],
): SagaIterator<CatalogItem[]> {
  const items: CatalogItem[] = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const ratePlan of ratePlans) {
    try {
      const item = (yield call(
        api.fetch,
        `${window.ORDERS}/catalog/${ratePlan}`,
        { throwError: true, expectedStatus: [404] },
      )) as CatalogItem;
      items.push(item);
    } catch (error) {
      if (error instanceof ApiError && error.code === 404) {
        yield call(removeItem, ratePlan);
      } else {
        yield put(setError('', error, { ratePlan }));
      }
    }
  }
  return items;
}

export function* setItemQuantity(id: string, qty: number): SagaIterator<void> {
  yield call(api.fetch, `${window.ORDERS}/cart/product/${id}`, {
    method: 'PATCH',
    parseJSON: false,
    body: JSON.stringify({ quantity: qty }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export function* changeCart(data: CartPatch): SagaIterator<void> {
  yield call(api.fetch, `${window.ORDERS}/cart`, {
    method: 'PATCH',
    parseJSON: false,
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export function* deleteCart(): SagaIterator<void> {
  yield call(api.fetch, `${window.ORDERS}/cart`, {
    method: 'DELETE',
    parseJSON: false,
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export function* addItem(
  ratePlan: string,
  quantity: number,
): SagaIterator<void> {
  yield call(api.fetch, `${window.ORDERS}/cart/product`, {
    method: 'POST',
    parseJSON: false,
    body: JSON.stringify({
      ratePlan,
      quantity,
    }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

/**
 * A migration's row is matched only with its `sourceRatePlan` too. Without it,
 * the API removes nothing and still answers 204.
 */
export function* removeItem(
  ratePlan: string,
  sourceRatePlan?: string,
): SagaIterator<void> {
  yield call(api.fetch, `${window.ORDERS}/cart/product`, {
    method: 'DELETE',
    parseJSON: false,
    body: JSON.stringify({
      ratePlan,
      sourceRatePlan,
    }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export function* swapItem(
  current: { ratePlan: string; quantity: number },
  swapWith: { ratePlan: string },
): SagaIterator<void> {
  yield call(removeItem, current.ratePlan);
  yield call(addItem, swapWith.ratePlan, current.quantity);
}

export function* getItemCatalogData(
  ratePlan: string,
): SagaIterator<CatalogItem> {
  return (yield call(api.fetch, `${window.ORDERS}/catalog/${ratePlan}`, {
    throwError: true,
  })) as CatalogItem;
}

export function* getPreview(
  data: PreviewRequest,
): SagaIterator<Preview | null> {
  return (yield call(api.fetch, `${window.ORDERS}/subscriptions/preview`, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
    },
  })) as Preview | null;
}

/**
 * For a trial, a 404 throws an unreported `ApiError` so the caller can drop it
 * from the cart, like `getCatalogData` does. Trials aren't meant for this cart,
 * so a missing description isn't worth reporting.
 */
export function* getProductCmsData(
  ratePlan: string,
  trial = false,
): SagaIterator<ProductCmsData | null> {
  return (yield call(
    api.fetch,
    `${window.ORDERS}/catalog/${ratePlan}/description`,
    trial
      ? { inlineError: true, throwError: true, expectedStatus: [404] }
      : { inlineError: true },
  )) as ProductCmsData | null;
}

export function* getTrialDescription(
  trialName: string,
): SagaIterator<TrialCmsData> {
  return (yield call(
    api.fetch,
    `${window.ORDERS}/catalog/${encodeURIComponent(trialName)}/description`,
    { throwError: true, inlineError: true },
  )) as TrialCmsData;
}

export function* getPageSignature(
  pageId: string,
): SagaIterator<PageSignature | null> {
  return (yield call(api.fetch, `${window.ORDERS}/payments/page-signature`, {
    method: 'POST',
    body: JSON.stringify({ pageId }),
    headers: {
      'Content-Type': 'application/json',
    },
    inlineError: true,
  })) as PageSignature | null;
}

export function* initPayPal(
  isUsa: boolean,
  isJapan: boolean,
): SagaIterator<PayPalInitResponse | null> {
  return (yield call(api.fetch, `${window.ORDERS}/paypal/init`, {
    method: 'POST',
    body: JSON.stringify({ isUsa, isJapan }),
    headers: {
      'Content-Type': 'application/json',
    },
    inlineError: true,
  })) as PayPalInitResponse | null;
}

export function* getPayPalBaid(
  token: string,
  isUsa: boolean,
  isJapan: boolean,
): SagaIterator<PayPalBaidResponse | null> {
  return (yield call(api.fetch, `${window.ORDERS}/paypal/baid`, {
    method: 'POST',
    body: JSON.stringify({ token, isUsa, isJapan }),
    headers: {
      'Content-Type': 'application/json',
    },
    inlineError: true,
  })) as PayPalBaidResponse | null;
}

export function* createOrder(
  request: OrderRequest,
): SagaIterator<Order | OrderFault | null> {
  return (yield call(api.fetch, `${window.ORDERS}/subscriptions`, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: {
      'Content-Type': 'application/json',
    },
    inlineError: true,
    parseErrorJSON: true,
    knownError: getPaymentDecline,
  })) as Order | OrderFault | null;
}

/** No `Content-Type` header — the browser sets the multipart boundary. */
export function* submitEduProof(formData: FormData): SagaIterator<void> {
  yield call(api.fetch, `${window.ORDERS}/edu/proof`, {
    method: 'POST',
    parseJSON: false,
    body: formData,
    throwError: true,
  });
}
